import type { AppLocale } from '../messages';

/**
 * Строки участка «chat». Свой файл у каждого участка, а не общий `messages.ts` на 4300 строк:
 * участки правятся параллельно, и общий файл давал бы конфликт на каждой новой строке.
 * Ключ добавляется во все четыре словаря — `en` задаёт набор, остальные проверяются типом.
 */
const en = {
  chatDeliverySent: 'Sent',
  chatDeliveryRead: 'Read',
  chatReactionMore: 'More reactions',
  chatReactionGroupFaces: 'Faces',
  chatReactionGroupHands: 'Hands',
  chatReactionGroupSymbols: 'Symbols',
  chatChannelRoleAdmin: 'Administrator',
  chatChannelRoleEditor: 'Editor',
  chatChannelRoleSubscriber: 'Subscriber',
  chatChannelYourRole: 'Your role: {role}',
  chatToday: 'Today',
  chatYesterday: 'Yesterday',
  chatNewMessages: 'New messages',
  chatDraftPrefix: 'Draft',
  chatSearchOpen: 'Search in chat',
  chatSearchPlaceholder: 'Search messages',
  chatSearchCounter: '{index} of {count}',
  chatSearchNoResults: 'Nothing found',
  chatSearchOlder: 'Older match',
  chatSearchNewer: 'Newer match',
  chatSearchClose: 'Close search',
  chatSearchFailed: 'Could not search messages',
  chatFailedTapHint: 'tap to retry',
  chatFailedDeleteLocal: 'Delete',
  chatMessageDeleted: 'Message deleted',
  chatForwardTitle: 'Forward to…',
  chatForwardSearchPlaceholder: 'Search chats and people',
  chatForwardRecent: 'Chats',
  chatForwardPeople: 'People',
  chatForwardLoadFailed: 'Could not load chats',
  chatForwarded: 'Forwarded',
  chatAttachGallery: 'Gallery',
  chatAttachCamera: 'Camera',
  chatAttachFile: 'File',
  chatAttachVideoMessage: 'Video message',
  chatAttachArticle: 'Article',
  chatFileOpenFailed: 'Could not open the file',
  chatFileTooLarge: 'The file is too large: up to {size}',
  chatFileSendFailed: 'Could not send the file',
  chatVoiceHoldHint: 'Hold to record, release to send',
  chatVoiceSlideCancel: '‹ Slide to cancel',
  chatVoiceLockHint: '↑ Lock',
  chatVoiceTooShort: 'Too short — hold the mic a little longer',
  chatSavedMore: 'More actions',
  chatReadByAction: 'Read by',
  chatReadByNobody: 'Nobody has read it yet',
  chatReadByFailed: 'Could not load who read it',
  chatOpenComments: 'Open comments',
  chatPostImageUploadFailed: 'Could not upload the image',
  chatEditShowOriginal: 'Show the original message',
  chatGalleryVideoFailed: 'Could not send the video',
} as const satisfies Record<string, string>;

export type ChatMessageKey = keyof typeof en;

export const chatMessages: Record<AppLocale, Record<ChatMessageKey, string>> = {
  en,
  ru: {
    chatDeliverySent: 'Отправлено',
    chatDeliveryRead: 'Прочитано',
    chatReactionMore: 'Другие реакции',
    chatReactionGroupFaces: 'Лица',
    chatReactionGroupHands: 'Жесты',
    chatReactionGroupSymbols: 'Символы',
    chatChannelRoleAdmin: 'Администратор',
    chatChannelRoleEditor: 'Редактор',
    chatChannelRoleSubscriber: 'Подписчик',
    chatChannelYourRole: 'Ваша роль: {role}',
    chatToday: 'Сегодня',
    chatYesterday: 'Вчера',
    chatNewMessages: 'Новые сообщения',
    chatDraftPrefix: 'Черновик',
    chatSearchOpen: 'Поиск в чате',
    chatSearchPlaceholder: 'Поиск сообщений',
    chatSearchCounter: '{index} из {count}',
    chatSearchNoResults: 'Ничего не найдено',
    chatSearchOlder: 'Предыдущее совпадение',
    chatSearchNewer: 'Следующее совпадение',
    chatSearchClose: 'Закрыть поиск',
    chatSearchFailed: 'Не удалось выполнить поиск',
    chatFailedTapHint: 'нажмите, чтобы повторить',
    chatFailedDeleteLocal: 'Удалить',
    chatMessageDeleted: 'Сообщение удалено',
    chatForwardTitle: 'Переслать…',
    chatForwardSearchPlaceholder: 'Поиск чатов и людей',
    chatForwardRecent: 'Чаты',
    chatForwardPeople: 'Люди',
    chatForwardLoadFailed: 'Не удалось загрузить чаты',
    chatForwarded: 'Переслано',
    chatAttachGallery: 'Галерея',
    chatAttachCamera: 'Камера',
    chatAttachFile: 'Файл',
    chatAttachVideoMessage: 'Видеосообщение',
    chatAttachArticle: 'Статья',
    chatFileOpenFailed: 'Не удалось открыть файл',
    chatFileTooLarge: 'Файл слишком большой: не больше {size}',
    chatFileSendFailed: 'Не удалось отправить файл',
    chatVoiceHoldHint: 'Удерживайте, чтобы записать, отпустите — чтобы отправить',
    chatVoiceSlideCancel: '‹ Влево — отмена',
    chatVoiceLockHint: '↑ Закрепить',
    chatVoiceTooShort: 'Слишком коротко — подержите микрофон подольше',
    chatSavedMore: 'Ещё',
    chatReadByAction: 'Прочитали',
    chatReadByNobody: 'Пока никто не прочитал',
    chatReadByFailed: 'Не удалось узнать, кто прочитал',
    chatOpenComments: 'Открыть комментарии',
    chatPostImageUploadFailed: 'Не удалось загрузить картинку',
    chatEditShowOriginal: 'Показать исходное сообщение',
    chatGalleryVideoFailed: 'Не удалось отправить видео',
  },
  sr: {
    chatDeliverySent: 'Poslato',
    chatDeliveryRead: 'Pročitano',
    chatReactionMore: 'Još reakcija',
    chatReactionGroupFaces: 'Lica',
    chatReactionGroupHands: 'Gestovi',
    chatReactionGroupSymbols: 'Simboli',
    chatChannelRoleAdmin: 'Administrator kanala',
    chatChannelRoleEditor: 'Urednik',
    chatChannelRoleSubscriber: 'Pretplatnik',
    chatChannelYourRole: 'Vaša uloga: {role}',
    chatToday: 'Danas',
    chatYesterday: 'Juče',
    chatNewMessages: 'Nove poruke',
    chatDraftPrefix: 'Nacrt',
    chatSearchOpen: 'Pretraga u četu',
    chatSearchPlaceholder: 'Pretraži poruke',
    chatSearchCounter: '{index} od {count}',
    chatSearchNoResults: 'Ništa nije pronađeno',
    chatSearchOlder: 'Prethodno poklapanje',
    chatSearchNewer: 'Sledeće poklapanje',
    chatSearchClose: 'Zatvori pretragu',
    chatSearchFailed: 'Pretraga nije uspela',
    chatFailedTapHint: 'dodirnite da pokušate ponovo',
    chatFailedDeleteLocal: 'Obriši',
    chatMessageDeleted: 'Poruka je obrisana',
    chatForwardTitle: 'Prosledi…',
    chatForwardSearchPlaceholder: 'Pretraga četova i ljudi',
    chatForwardRecent: 'Četovi',
    chatForwardPeople: 'Ljudi',
    chatForwardLoadFailed: 'Učitavanje četova nije uspelo',
    chatForwarded: 'Prosleđeno',
    chatAttachGallery: 'Galerija',
    chatAttachCamera: 'Kamera',
    chatAttachFile: 'Fajl',
    chatAttachVideoMessage: 'Video poruka',
    chatAttachArticle: 'Članak',
    chatFileOpenFailed: 'Fajl nije moguće otvoriti',
    chatFileTooLarge: 'Fajl je prevelik: najviše {size}',
    chatFileSendFailed: 'Slanje fajla nije uspelo',
    chatVoiceHoldHint: 'Držite da snimate, pustite da pošaljete',
    chatVoiceSlideCancel: '‹ Levo — otkaži',
    chatVoiceLockHint: '↑ Zaključaj',
    chatVoiceTooShort: 'Prekratko — držite mikrofon malo duže',
    chatSavedMore: 'Još',
    chatReadByAction: 'Pročitali su',
    chatReadByNobody: 'Još niko nije pročitao',
    chatReadByFailed: 'Nije moguće saznati ko je pročitao',
    chatOpenComments: 'Otvori komentare',
    chatPostImageUploadFailed: 'Otpremanje slike nije uspelo',
    chatEditShowOriginal: 'Prikaži originalnu poruku',
    chatGalleryVideoFailed: 'Slanje videa nije uspelo',
  },
  fr: {
    chatDeliverySent: 'Envoyé',
    chatDeliveryRead: 'Lu',
    chatReactionMore: 'Plus de réactions',
    chatReactionGroupFaces: 'Visages',
    chatReactionGroupHands: 'Gestes',
    chatReactionGroupSymbols: 'Symboles',
    chatChannelRoleAdmin: 'Administrateur',
    chatChannelRoleEditor: 'Éditeur',
    chatChannelRoleSubscriber: 'Abonné',
    chatChannelYourRole: 'Votre rôle : {role}',
    chatToday: "Aujourd'hui",
    chatYesterday: 'Hier',
    chatNewMessages: 'Nouveaux messages',
    chatDraftPrefix: 'Brouillon',
    chatSearchOpen: 'Rechercher dans la discussion',
    chatSearchPlaceholder: 'Rechercher des messages',
    chatSearchCounter: '{index} sur {count}',
    chatSearchNoResults: 'Aucun résultat',
    chatSearchOlder: 'Résultat précédent',
    chatSearchNewer: 'Résultat suivant',
    chatSearchClose: 'Fermer la recherche',
    chatSearchFailed: 'La recherche a échoué',
    chatFailedTapHint: 'touchez pour réessayer',
    chatFailedDeleteLocal: 'Supprimer',
    chatMessageDeleted: 'Message supprimé',
    chatForwardTitle: 'Transférer à…',
    chatForwardSearchPlaceholder: 'Rechercher des discussions et des personnes',
    chatForwardRecent: 'Discussions',
    chatForwardPeople: 'Personnes',
    chatForwardLoadFailed: 'Impossible de charger les discussions',
    chatForwarded: 'Transféré',
    chatAttachGallery: 'Galerie',
    chatAttachCamera: 'Appareil photo',
    chatAttachFile: 'Fichier',
    chatAttachVideoMessage: 'Message vidéo',
    chatAttachArticle: 'Publication',
    chatFileOpenFailed: "Impossible d'ouvrir le fichier",
    chatFileTooLarge: 'Fichier trop volumineux : {size} maximum',
    chatFileSendFailed: "Impossible d'envoyer le fichier",
    chatVoiceHoldHint: 'Maintenez pour enregistrer, relâchez pour envoyer',
    chatVoiceSlideCancel: '‹ Glisser pour annuler',
    chatVoiceLockHint: '↑ Verrouiller',
    chatVoiceTooShort: 'Trop court — maintenez le micro un peu plus longtemps',
    chatSavedMore: 'Plus',
    chatReadByAction: 'Lu par',
    chatReadByNobody: "Personne ne l'a encore lu",
    chatReadByFailed: 'Impossible de savoir qui l’a lu',
    chatOpenComments: 'Ouvrir les commentaires',
    chatPostImageUploadFailed: "Impossible d'envoyer l'image",
    chatEditShowOriginal: 'Afficher le message d’origine',
    chatGalleryVideoFailed: "Impossible d'envoyer la vidéo",
  },
};
